import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Patient, Sample, SampleStatus } from '../../types';
import { User, ArrowLeft, Activity, Clock, TestTube, FileText, CheckCircle, Archive } from 'lucide-react';
import { Calendar } from 'lucide-react';

interface PatientDetailsProps {
    patient: Patient;
    onBack: () => void;
}

const PatientDetails: React.FC<PatientDetailsProps> = ({ patient, onBack }) => {
    const [patientHistory, setPatientHistory] = useState<Sample[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!isSupabaseConfigured()) {
                 // Mock history
                 setPatientHistory([
                    { id: '101', patient_id: patient.id, sample_type: 'Sang', status: 'validated', collection_date: '2023-11-15', created_at: '', analyses: ['Microscopie'] },
                    { id: '102', patient_id: patient.id, sample_type: 'Ecouvillon', status: 'processing', collection_date: '2023-12-01', created_at: '', analyses: ['GeneXpert'] }
                ] as Sample[]);
            } else {
                const { data } = await supabase
                    .from('samples')
                    .select('*')
                    .eq('patient_id', patient.id)
                    .order('collection_date', { ascending: false });
                
                if (data) setPatientHistory(data as Sample[]);
            }
            setLoadingHistory(false);
        };
        fetchHistory();
    }, [patient.id]);

    const getStatusBadge = (status: SampleStatus) => {
        switch (status) {
          case 'received': return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center gap-1"><TestTube size={12}/> Reçu</span>;
          case 'processing': return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold flex items-center gap-1"><Clock size={12}/> En cours</span>;
          case 'validated': return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1"><CheckCircle size={12}/> Validé</span>;
          case 'archived': return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold flex items-center gap-1"><Archive size={12}/> Archivé</span>;
        }
      };

    return (
        <div className="pb-20 md:pb-0">
            {/* Sticky Header for Details */}
            <div className="sticky top-0 z-10 bg-slate-100 -mt-4 -mx-4 px-4 pt-4 md:-mt-8 md:-mx-8 md:px-8 md:pt-8 pb-4">
                <button 
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors mb-4 font-medium"
                >
                    <ArrowLeft size={20} /> Retour à la liste
                </button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                        <User className="text-indigo-600" size={28}/>
                        {patient.last_name.toUpperCase()} {patient.first_name}
                    </h2>
                    <div className="flex items-center gap-3">
                        <span className="bg-white px-3 py-1 rounded border border-slate-200 text-sm font-mono text-slate-600">
                            {patient.unique_id}
                        </span>
                        <span className={`px-3 py-1 rounded text-sm font-bold ${patient.gender === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                            {patient.gender === 'M' ? 'Masculin' : 'Féminin'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Info Card */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-fit">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <FileText size={18} className="text-slate-400"/>
                        Informations
                    </h3>
                    <div className="space-y-4 text-sm">
                        <div>
                            <span className="block text-slate-500 text-xs uppercase font-bold tracking-wider">Date de naissance</span>
                            <span className="text-slate-800 font-medium flex items-center gap-2 mt-1">
                                <Calendar size={14} className="text-indigo-500"/>
                                {patient.birth_date}
                            </span>
                        </div>
                        <div>
                            <span className="block text-slate-500 text-xs uppercase font-bold tracking-wider">Âge estimé</span>
                            <span className="text-slate-800 font-medium">
                                {new Date().getFullYear() - new Date(patient.birth_date).getFullYear()} ans
                            </span>
                        </div>
                        <div>
                            <span className="block text-slate-500 text-xs uppercase font-bold tracking-wider">Enregistré le</span>
                            <span className="text-slate-800">
                                {new Date(patient.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* History Card */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <Activity size={18} className="text-slate-400"/>
                        Historique des Prélèvements
                    </h3>

                    {loadingHistory ? (
                        <div className="text-center py-8 text-slate-400">Chargement de l'historique...</div>
                    ) : patientHistory.length > 0 ? (
                        <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-slate-200 before:opacity-50">
                            {patientHistory.map((sample) => (
                                <div key={sample.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                    {/* Icon on Timeline */}
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-100 group-hover:bg-indigo-50 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-slate-500">
                                        <TestTube size={18}/>
                                    </div>
                                    
                                    {/* Content Card */}
                                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-bold text-indigo-900">{sample.sample_type}</span>
                                            {getStatusBadge(sample.status)}
                                        </div>
                                        <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                                            <Clock size={12}/>
                                            {new Date(sample.collection_date).toLocaleDateString()} à {new Date(sample.collection_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </div>
                                        {sample.analyses && sample.analyses.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2 border-t border-slate-200 pt-2">
                                                {sample.analyses.map(a => (
                                                    <span key={a} className="text-[10px] uppercase font-bold text-slate-500 bg-white px-1 border rounded">{a}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                            <TestTube size={32} className="mx-auto text-slate-300 mb-2"/>
                            <p className="text-slate-500">Aucun prélèvement enregistré pour ce patient.</p>
                            <button 
                                className="mt-4 text-indigo-600 font-medium text-sm hover:underline"
                                onClick={() => {/* Logic to create sample for this patient could go here */}}
                            >
                                Créer un nouveau prélèvement
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PatientDetails;
